import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Bell, Download, X } from "lucide-react";
import { currentSubscription, enablePush, pushPermission, pushSupported } from "../api/push";
import { usePwa } from "../context/PwaContext";
import { ru } from "../locales/ru";

const KEY = "poehali_push_prompt_snoozed_at";
const SNOOZE_DAYS = 7;

const snoozed = () => {
  const at = Number(localStorage.getItem(KEY) || 0);
  return at > 0 && Date.now() - at < SNOOZE_DAYS * 24 * 60 * 60 * 1000;
};

export const PushOnboardingBanner = () => {
  const { eligible, needsInstall, autoBlocked, install, showPushInstructions, permission, setBannerHeight } = usePwa();
  const offeredRef = useRef(false);
  const bannerRef = useRef(null);
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!eligible || autoBlocked || (!needsInstall && (!pushSupported() || pushPermission() === "granted"))) return;
    let cancelled = false;
    const onInteract = async () => {
      if (offeredRef.current || snoozed()) return;
      offeredRef.current = true;
      const sub = needsInstall ? null : await currentSubscription().catch(() => null);
      if (!cancelled && !sub && (needsInstall || pushPermission() !== "granted")) setVisible(true);
    };
    window.addEventListener("click", onInteract);
    window.addEventListener("keyup", onInteract);
    return () => {
      cancelled = true;
      window.removeEventListener("click", onInteract);
      window.removeEventListener("keyup", onInteract);
    };
  }, [eligible, needsInstall, autoBlocked]);

  useEffect(() => {
    const banner = bannerRef.current;
    if (!banner) { setBannerHeight(0); return; }
    const measure = () => setBannerHeight(banner.getBoundingClientRect().height + 12);
    measure();
    const observer = new ResizeObserver(measure); observer.observe(banner);
    return () => { observer.disconnect(); setBannerHeight(0); };
  }, [visible, permission, needsInstall, autoBlocked, setBannerHeight]);

  if (!visible || autoBlocked || (!needsInstall && permission === "granted")) return null;

  const later = () => {
    localStorage.setItem(KEY, String(Date.now()));
    offeredRef.current = false;
    setVisible(false);
  };

  const allow = async () => {
    if (needsInstall) { await install(); setVisible(false); return; }
    if (pushPermission() === "denied") { showPushInstructions(); setVisible(false); return; }
    setBusy(true);
    try {
      await enablePush();
      window.dispatchEvent(new Event("push-state-changed"));
      toast.success(ru.pwa.pushOn);
      setVisible(false);
    } catch (err) {
      if (err?.message === "denied") {
        showPushInstructions(); window.dispatchEvent(new Event("push-state-changed")); setVisible(false);
      } else if (err?.message === "dismissed") {
        setVisible(false);
      } else {
        toast.error(ru.errors.generic);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      ref={bannerRef}
      data-testid={needsInstall ? "push-install-banner" : "push-onboarding-banner"}
      className="relative mx-3 mt-3 shrink-0 bg-black text-white rounded-2xl px-4 py-3.5 shadow-lg"
    >
      <div className="flex items-start gap-3">
        <span className="bg-white/10 rounded-full p-2 shrink-0">
          {needsInstall ? <Download size={15} /> : <Bell size={15} />}
        </span>
        <p data-testid="push-prompt-text" className="text-sm font-semibold leading-snug flex-1">{needsInstall ? ru.pwa.installForPush : ru.pwa.promptText}</p>
        <button data-testid="push-prompt-close" onClick={later} className="shrink-0 p-1.5 rounded-full bg-white/10">
          <X size={13} />
        </button>
      </div>
      <div className="flex items-center gap-2 mt-3">
        <button
          data-testid="push-prompt-allow"
          onClick={allow}
          disabled={busy}
          className="flex-1 h-10 bg-white text-black rounded-full text-sm font-bold active:scale-[0.97] transition-transform disabled:opacity-50"
        >
          {needsInstall ? ru.pwa.install : permission === "denied" ? ru.pwa.instructions : ru.pwa.promptAllow}
        </button>
        <button
          data-testid="push-prompt-later"
          onClick={later}
          className="flex-1 h-10 bg-white/10 rounded-full text-sm font-semibold active:scale-[0.97] transition-transform"
        >
          {ru.pwa.promptLater}
        </button>
      </div>
    </div>
  );
};
